import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * BrandContext
 *
 * - Fetches /api/site/settings (public).
 * - Applies the chosen palette globally by injecting a <style> tag that
 *   overrides every Tailwind utility using the hardcoded #FF3E00 with the
 *   current palette accent color. This avoids a huge JSX refactor across
 *   the codebase while still giving the superadmin live control.
 * - Exposes `refresh()` so the admin panel can re-pull settings after save.
 */
const BrandContext = createContext(null);

const DEFAULT_PALETTE = { accent: "#FF3E00", accent_soft: "#FF6C30" };
const LEGACY_ACCENT = "#FF3E00";
const STYLE_ID = "elarte-brand-overrides";

function applyPalette(accent, accentSoft) {
  // Keep a CSS var so custom CSS blocks can opt-in.
  document.documentElement.style.setProperty("--accent", accent);
  document.documentElement.style.setProperty("--accent-soft", accentSoft);

  // Build overrides for every Tailwind arbitrary-value utility that uses the
  // legacy brutalist orange. Using an attribute selector covers every variant
  // (bg/text/border/fill/hover:bg-*/focus:border-*/placeholder:text-*/etc).
  const css = `
    [class*="[#FF3E00]"] {}
    .bg-\\[\\#FF3E00\\] { background-color: ${accent} !important; }
    .text-\\[\\#FF3E00\\] { color: ${accent} !important; }
    .border-\\[\\#FF3E00\\] { border-color: ${accent} !important; }
    .fill-\\[\\#FF3E00\\] { fill: ${accent} !important; }
    .stroke-\\[\\#FF3E00\\] { stroke: ${accent} !important; }
    .hover\\:bg-\\[\\#FF3E00\\]:hover { background-color: ${accent} !important; }
    .hover\\:text-\\[\\#FF3E00\\]:hover { color: ${accent} !important; }
    .hover\\:border-\\[\\#FF3E00\\]:hover { border-color: ${accent} !important; }
    .focus\\:border-\\[\\#FF3E00\\]:focus { border-color: ${accent} !important; }
    .focus-within\\:border-\\[\\#FF3E00\\]:focus-within { border-color: ${accent} !important; }
    .placeholder\\:text-\\[\\#FF3E00\\]::placeholder { color: ${accent} !important; }
    .group-hover\\:text-\\[\\#FF3E00\\]:is(.group:hover *) { color: ${accent} !important; }
    .accent-\\[\\#FF3E00\\] { accent-color: ${accent} !important; }
  `;
  let tag = document.getElementById(STYLE_ID);
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

export function BrandProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [palettes, setPalettes] = useState({});
  const [disciplines, setDisciplines] = useState([]);

  const refresh = async () => {
    try {
      const [settingsRes, discRes] = await Promise.all([
        api.get("/site/settings"),
        api.get("/disciplines").catch(() => ({ data: { disciplines: [] } })),
      ]);
      const data = settingsRes.data;
      setSettings(data.settings || null);
      setPalettes(data.palettes || {});
      setDisciplines(discRes.data.disciplines || []);
      const meta = data.palette_meta || DEFAULT_PALETTE;
      applyPalette(meta.accent || LEGACY_ACCENT, meta.accent_soft || LEGACY_ACCENT);
      if (data.settings?.site_name) {
        document.title = data.settings.site_name;
      }
    } catch (_) {
      applyPalette(LEGACY_ACCENT, LEGACY_ACCENT);
    }
  };

  useEffect(() => {
    applyPalette(LEGACY_ACCENT, LEGACY_ACCENT); // instant paint
    refresh();
  }, []);

  const value = useMemo(
    () => ({ settings: settings || {}, palettes, disciplines, refresh }),
    [settings, palettes, disciplines]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export const useBrand = () => useContext(BrandContext) || { settings: {}, palettes: {}, disciplines: [], refresh: () => {} };
